import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";
import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from "react-bootstrap";
import type { Options } from "highcharts";
import AccordionComponent from "../../../../../common/Accordion";
import MonthIntervalDropdown from "../../../../../common/MonthIntervalDropdown";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartExportButton from "../../../../../common/HighchartExportButton";
import { getHRESummaryByQMEDoctor } from "../../../../../services/ActionStatusApi";
import TopLimitDropdown from "../../../../../common/TopLimitDropdown";

const doctorColors = [
  "#9b6d15",
  "#0d6efd",
  "#198754",
  "#dc3545",
  "#6f42c1",
  "#fd7e14",
  "#20c997",
  "#6610f2",
  "#0dcaf0",
  "#d63384",
];

interface DoctorWiseSummaryProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const DoctorWiseSummary: React.FC<DoctorWiseSummaryProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [topCount, setTopCount] = useState<number>(5);
  const [monthInterval, setMonthInterval] = useState<string>("1");
  const [topLabel, setTopLabel] = useState<string>("Top 5");
  const [intervalLabel, setIntervalLabel] = useState<string>("This Month");
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [activeRole, setActiveRole] = useState("History");
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const fetchHRESummaryData = async () => {
    try {
      const result = await getHRESummaryByQMEDoctor(topCount, monthInterval);
      // setSummaryData(data);
      setSummaryData(result);
    } catch (error) {
      console.error("Failed to load case summary data", error);
    }
  };

  useEffect(() => {
    fetchHRESummaryData();
  }, [topCount, monthInterval]);

  useEffect(() => {
    if (summaryData?.length > 0) {
      prepareDoctorChart(summaryData, activeRole);
    }
  }, [summaryData, activeRole]);

  const prepareDoctorChart = (data: any[], role: string) => {
    const getCountByRole = (item: any) => {
      if (role === "History") return item.historyCount;
      if (role === "ROR") return item.rorCount;
      if (role === "Editor") return item.editorCount;
      return 0;
    };

    setChartOptions({
      chart: {
        type: "column",
        height: 350,
      },
      title: { text: undefined },

      xAxis: {
        categories: data.map((d) => d.qmeDoctorName),
        labels: {
          useHTML: true,
          rotation: 0,
          autoRotation: [],
          style: {
            fontSize: "11px",
            textAlign: "center",
          },
          formatter: function () {
            const rawValue = this.value;
            const name = rawValue ? String(rawValue) : "";
            const words = name.split(" ");

            let formattedName = name;

            if (words.length > 2) {
              formattedName =
                words.slice(0, 2).join(" ") +
                "<br/>" +
                words.slice(2).join(" ");
            }

            return `<div style="width:80px; text-align:center; white-space:normal;">
              ${formattedName}
            </div>`;
          },
        },
      },

      // Left Axis → Numbers
      yAxis: {
        min: 0,
        max: 120,
        tickInterval: 20,
        title: { text: "Count" },
      },

      tooltip: {
        pointFormat: `<b>{point.y}</b> ${role} cases`,
      },

      plotOptions: {
        column: {
          pointWidth: 25,
          dataLabels: {
            enabled: true,
          },
          borderRadius: 4, // modern look
        },
      },

      credits: { enabled: false },
      legend: { enabled: false },

      series: [
        {
          name: role,
          type: "column",
          data: data.map((d, index) => ({
            y: getCountByRole(d),
            color: doctorColors[index % doctorColors.length],
          })),
        },
      ],
    });
  };

  const handleTopChange = (value: number, label: string) => {
    setTopCount(value);
    setTopLabel(label);
  };

  const handleIntervalChange = (value: string | number, label: string) => {
    setMonthInterval(String(value));
    setIntervalLabel(label);
  };

  const [caseSummaryKey, setCaseSummaryKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (caseSummaryKey === key) {
      return; // do nothing
    }
    setCaseSummaryKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={caseSummaryKey}
      onSelect={handleBodyAccordionSelect}
      isDashboard={true}
    />
  );

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    const closeAll = () => setOpenDropdown(null);
    window.addEventListener("closeAllDropdowns", closeAll);
    return () => window.removeEventListener("closeAllDropdowns", closeAll);
  }, []);

  const hasValidDoctorData =
    summaryData &&
    summaryData.length > 0 &&
    summaryData.some(
      (item) => item.qmeDoctorContactId && item.qmeDoctorContactId !== 0,
    );

  return (
    <div className="bg-white rounded-lg shadow-md h-100">
      {summaryAccordion([
        {
          title: (
            <div
              className="d-flex  align-items-center w-100 "
              onClick={(e) => e.stopPropagation()}
            >
              <span className="me-auto">Doctor-wise Summary Report</span>

              <div className="d-flex gap-2">
                <>
                  <TopLimitDropdown
                    title={topLabel}
                    onSelect={handleTopChange}
                    show={openDropdown === "doctorWiseSummary"}
                    onToggle={(isOpen) =>
                      setOpenDropdown(isOpen ? "doctorWiseSummary" : null)
                    }
                  />
                </>

                <MonthIntervalDropdown
                  title={intervalLabel}
                  onSelect={handleIntervalChange}
                  show={openDropdown === "doctorWiseInterval"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "doctorWiseInterval" : null)
                  }
                />
              </div>
              <HighchartExportButton chartRef={chartComponentRef} />
            </div>
          ),
          content: (
            <>
              <Row className="justify-content-center mb-3">
                <Col xs="auto">
                  <div className="d-flex gap-2">
                    {["History", "ROR", "Editor"].map((role) => (
                      <button
                        key={role}
                        onClick={() => setActiveRole(role)}
                        style={{
                          padding: "3px 18px",
                          borderRadius: "30px",

                          border: "1px solid #0192d5",
                          backgroundColor:
                            activeRole === role ? "#0192d5" : "#F4F8FE",
                          color: activeRole === role ? "#ffffff" : "#0192d5",
                          fontWeight: 500,
                          transition: "all 0.2s ease",
                        }}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </Col>
              </Row>
              {hasValidDoctorData ? (
                <HighchartsReact
                  highcharts={Highcharts}
                  options={chartOptions}
                  ref={chartComponentRef}
                />
              ) : (
                <div className="text-center">
                  {/* <img src="/noChart.png" alt="noChart" className="nodata" /> */}
                  <div className="nodata_wrapper">
                    <span className="material-symbols-outlined">
                      bar_chart_off
                    </span>{" "}
                    No Data Available
                  </div>
                </div>
              )}
            </>
          ),
        },
      ])}
    </div>
  );
};

export default DoctorWiseSummary;
